/**
 * Utilitário para tradução de nomenclaturas de investimentos
 * Permite alterar labels no frontend sem modificar o backend
 */

export type InvestmentType = 'trade' | 'private';

/**
 * Mapeia o tipo de investimento do backend para o label exibido no frontend
 */
export const investmentTypeLabels: Record<InvestmentType, string> = {
  trade: 'Pool',
  private: 'Private',
};

/**
 * Retorna o label formatado para exibição no frontend
 * @param type - Tipo de investimento do backend ('trade' | 'private')
 * @returns Label traduzido para exibição
 */
export const getInvestmentTypeLabel = (type: InvestmentType): string => {
  return investmentTypeLabels[type] || type;
};

/**
 * Converte o label do frontend de volta para o tipo do backend
 * @param label - Label exibido no frontend
 * @returns Tipo usado no backend
 */
export const getInvestmentTypeFromLabel = (label: string): InvestmentType | undefined => {
  const entry = Object.entries(investmentTypeLabels).find(([_, value]) => value === label);
  return entry ? (entry[0] as InvestmentType) : undefined;
};

/**
 * Verifica se um investimento é do tipo Pool (Trade)
 */
export const isPoolInvestment = (type: InvestmentType): boolean => {
  return type === 'trade';
};

/**
 * Verifica se um investimento é do tipo Private
 */
export const isSCPInvestment = (type: InvestmentType): boolean => {
  return type === 'private';
};

/**
 * Retorna todas as opções de tipo de investimento para filtros/selects
 */
export const getInvestmentTypeOptions = () => {
  return Object.entries(investmentTypeLabels).map(([value, label]) => ({
    value,
    label,
  }));
};

/**
 * Status de investimento possíveis
 *
 * Para Private:
 * - em_breve: Private que ainda não abriu a captação
 * - em_captacao: Private que ainda tem cotas disponíveis
 * - gerando_recebiveis: Private que finalizou captação mas ainda não encerrou o prazo
 * - encerrado: Private que encerrou o prazo
 */
export type InvestmentStatus = 'ativo' | 'em_captacao' | 'em_breve' | 'gerando_recebiveis' | 'concluido' | 'encerrado' | 'pendente' | 'cancelado';

/**
 * Mapeia o status de investimento do backend para o label exibido no frontend
 */
export const investmentStatusLabels: Record<string, string> = {
  'ativo': 'Ativo',
  'em_captacao': 'Em Captação',
  'em_breve': 'Em Breve',
  'gerando_recebiveis': 'Gerando Recebíveis',
  'pausado': 'Pausado',
  'concluido': 'Concluído',
  'encerrado': 'Encerrado',
  'pendente': 'Pendente',
  'cancelado': 'Cancelado',
};

/**
 * Retorna o label formatado do status para exibição no frontend
 * @param status - Status do investimento do backend
 * @returns Label formatado para exibição
 */
export const getInvestmentStatusLabel = (status: string): string => {
  return investmentStatusLabels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Formata o prazo do investimento considerando que prazo mínimo pode ser opcional
 * @param minMonths - Prazo mínimo em meses (opcional)
 * @param maxMonths - Prazo máximo em meses (opcional)
 * @returns String formatada do prazo (ex: "12 meses" ou "12-24 meses")
 */
export const formatTermDuration = (minMonths?: number | null, maxMonths?: number | null): string => {
  // Se não houver prazo mínimo, mostrar apenas o máximo
  if (!minMonths || minMonths === 0) {
    return maxMonths ? `${maxMonths} meses` : '—';
  }
  
  // Se ambos existirem e forem iguais, mostrar apenas um valor
  if (minMonths === maxMonths) {
    return `${minMonths} meses`;
  }
  
  // Se ambos existirem e forem diferentes, mostrar range
  if (maxMonths && maxMonths > 0) {
    return `${minMonths}-${maxMonths} meses`;
  }
  
  // Se só houver mínimo
  return `${minMonths} meses`;
};

/**
 * Formata o prazo a partir de um objeto duration
 * @param duration - Objeto com min e max
 * @returns String formatada do prazo
 */
export const formatTermFromDuration = (duration: { min?: number | null; max?: number | null }): string => {
  return formatTermDuration(duration.min, duration.max);
};